import { useState, useEffect } from 'react';
import axios from 'axios';

// ACORD lives on the same host but at /acord/api/* — we can't use the
// InsureDocs `api` instance (baseURL: /api) because that would produce
// /api/acord/api/... Instead make a small dedicated client that uses the
// same JWT from localStorage. The token works against both apps thanks
// to shared SECRET_KEY auth.
const acordApi = axios.create({ baseURL: '/acord/api' });
acordApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Modal for adding/linking an ACORD client from an InsureDocs client view.
 *
 * Flow when opened:
 *   1. Auto-search acord.clients by InsureDocs name → suggestions with scores
 *   2. User picks one to "Link & Open" (with confirm if already linked elsewhere)
 *      OR clicks "Create new" (POST to /acord/api/clients with pre-filled data)
 *   3. On success: open ACORD in new tab with ?client=<insureId>
 *
 * Props:
 *   client: the InsureDocs client object (has id, name, phone1, email, etc.)
 *   onClose: callback
 *   darkMode: bool for theming
 */
export default function AddAcordModal({ client, onClose, darkMode }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(null); // {acordClient, existingLinkId}

  const bg = darkMode ? '#1a1d24' : '#fff';
  const fg = darkMode ? '#e8eaf0' : '#222';
  const borderColor = darkMode ? '#2a2e38' : '#ddd';
  const subtleBg = darkMode ? '#0d0f14' : '#f7f7f9';

  // Auto-search on mount
  useEffect(() => {
    if (!client?.name) return;
    setLoading(true);
    setError(null);
    acordApi.get('/clients/suggest', {
      params: { name: client.name, limit: 10 },
    })
      .then(r => setSuggestions(r.data))
      .catch(e => setError(e?.response?.data?.detail || e.message || 'Search failed'))
      .finally(() => setLoading(false));
  }, [client?.name]);

  const unlinkOne = async (acordClient) => {
    if (!window.confirm(`Unlink "${acordClient.full_name}" from this InsureDocs client?`)) return
    setBusy(true)
    setError(null)
    try {
      await acordApi.patch(`/clients/${acordClient.id}/link-insuredocs`, {
        insuredocs_client_id: null,
      })
      // Refresh the suggestions so the row's badge updates
      const r = await acordApi.get('/clients/suggest', {
        params: { name: client.name, limit: 10 },
      })
      setSuggestions(r.data)
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Unlink failed')
    } finally {
      setBusy(false)
    }
  }

  const linkAndOpen = async (acordClient, force) => {
    // If acord.client is already linked to a DIFFERENT InsureDocs client,
    // show confirmation before overwriting (unless force=true).
    if (!force && acordClient.insuredocs_client_id && acordClient.insuredocs_client_id !== client.id) {
      setConfirmOverwrite({ acordClient, existingLinkId: acordClient.insuredocs_client_id });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await acordApi.patch(`/clients/${acordClient.id}/link-insuredocs`, {
        insuredocs_client_id: client.id,
      });
      window.open(`/acord/?client=${client.id}`, '_blank');
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Link failed');
      setBusy(false);
    }
  };

  const createNew = async () => {
    setBusy(true);
    setError(null);
    // Pre-fill from InsureDocs data. Strip the leading docsvault number
    // (e.g. "5415 JAMES HUYNH 1.49 CLEANERS") if present. ACORD will accept
    // partial data and the user can fix it up there.
    const stripped = (client.name || '').replace(/^[X\d]+\s+/, '').trim();
    const payload = {
      full_name: stripped || client.name,
      primary_phone: client.phone1 || null,
      primary_email: client.email || null,
      primary_contact_name: client.first_name || null,
      address_line_one: client.address_name || null,
      insuredocs_client_id: client.id,
    };
    try {
      await acordApi.post('/clients', payload);
      window.open(`/acord/?client=${client.id}`, '_blank');
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Create failed');
      setBusy(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    }}
      onClick={onClose}>
      <div style={{
        background: bg, color: fg, borderRadius: 10, padding: 22, width: 580,
        maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>📄 Add to ACORD</div>
          <button onClick={onClose}
            style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: fg }}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: darkMode ? '#9aa0aa' : '#666', marginBottom: 14 }}>
          For InsureDocs client: <strong>{client.name}</strong>
        </div>

        {error && (
          <div style={{ background: '#fee', color: '#a33', padding: 8, borderRadius: 6, marginBottom: 12, fontSize: 12 }}>
            {error}
          </div>
        )}

        {/* SECTION 1: Matches */}
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
          {loading ? 'Searching ACORD clients…' : `Matches in ACORD (${suggestions.length})`}
        </div>
        {!loading && suggestions.length === 0 && (
          <div style={{ fontSize: 12, color: darkMode ? '#9aa0aa' : '#666', padding: 10, background: subtleBg, borderRadius: 6, marginBottom: 12 }}>
            No similar ACORD clients found. Use "Create new" below.
          </div>
        )}
        {!loading && suggestions.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            {suggestions.map(s => (
              <div key={s.id} style={{
                padding: 10, borderRadius: 6, background: subtleBg, marginBottom: 6,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.full_name}
                  </div>
                  <div style={{ fontSize: 11, color: darkMode ? '#9aa0aa' : '#888' }}>
                    Score: {s.score}
                    {s.insuredocs_client_id && (
                      <span style={{ marginLeft: 8, color: s.insuredocs_client_id === client.id ? '#27AE60' : '#d97706' }}>
                        {s.insuredocs_client_id === client.id
                          ? '✓ already linked to this client'
                          : `⚠ linked to InsureDocs #${s.insuredocs_client_id}`}
                      </span>
                    )}
                  </div>
                </div>
                {s.insuredocs_client_id === client.id ? (
                  <>
                    <button onClick={() => { window.open(`/acord/?client=${client.id}`, '_blank'); onClose() }} disabled={busy}
                      style={{
                        padding: '5px 10px', fontSize: 12, background: '#0f766e',
                        color: '#fff', border: 'none', borderRadius: 4, cursor: busy ? 'wait' : 'pointer',
                      }}>
                      Open
                    </button>
                    <button onClick={() => unlinkOne(s)} disabled={busy}
                      title="Remove the link between this ACORD client and the current InsureDocs client"
                      style={{
                        padding: '5px 10px', fontSize: 12, background: 'transparent',
                        color: '#c0392b', border: '1px solid #c0392b', borderRadius: 4, cursor: busy ? 'wait' : 'pointer', marginLeft: 4,
                      }}>
                      Unlink
                    </button>
                  </>
                ) : (
                  <button onClick={() => linkAndOpen(s)} disabled={busy}
                    style={{
                      padding: '5px 10px', fontSize: 12, background: '#0f766e',
                      color: '#fff', border: 'none', borderRadius: 4, cursor: busy ? 'wait' : 'pointer',
                    }}>
                    Link & Open
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* SECTION 2: Create new */}
        <div style={{ borderTop: `1px solid ${borderColor}`, paddingTop: 12, marginTop: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Or create new</div>
          <div style={{ fontSize: 11, color: darkMode ? '#9aa0aa' : '#666', marginBottom: 8 }}>
            Creates a new ACORD client pre-filled with this client's name, phone, email, and address.
            You can edit any field in ACORD afterwards.
          </div>
          <button onClick={createNew} disabled={busy}
            style={{
              padding: '7px 14px', fontSize: 13, background: '#0f766e', color: '#fff',
              border: 'none', borderRadius: 6, fontWeight: 600, cursor: busy ? 'wait' : 'pointer',
            }}>
            {busy ? 'Working…' : '+ Create new ACORD client'}
          </button>
        </div>

        {/* CONFIRM OVERWRITE DIALOG */}
        {confirmOverwrite && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
          }}>
            <div style={{ background: bg, color: fg, padding: 22, borderRadius: 10, width: 440 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>⚠ Already linked elsewhere</div>
              <div style={{ fontSize: 13, marginBottom: 14, lineHeight: 1.5 }}>
                ACORD client <strong>"{confirmOverwrite.acordClient.full_name}"</strong> is
                currently linked to InsureDocs client #{confirmOverwrite.existingLinkId}.
                <br /><br />
                If you proceed, the link will be re-pointed to <strong>{client.name}</strong>.
                The other client will lose this association.
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setConfirmOverwrite(null)}
                  style={{ padding: '6px 14px', fontSize: 12, border: `1px solid ${borderColor}`, background: subtleBg, color: fg, borderRadius: 6, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={() => { const c = confirmOverwrite.acordClient; setConfirmOverwrite(null); linkAndOpen(c, true); }}
                  style={{ padding: '6px 14px', fontSize: 12, background: '#d97706', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>
                  Re-point link to this client
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
